import React, { useState } from 'react';
import { CheckCircle, Circle, Loader2, X } from 'lucide-react';
import suiteVerificationService, { autoVerification, suiteVerificationKey, useSuiteVerifications } from '../services/suiteVerificationService';
import { getCoverage, useKnownFailures } from '../services/knownFailuresService';

const formatVerifiedAt = (iso) =>
  new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

/**
 * Indique si le run d'une suite (pipeline + build) a été passé en revue, par qui
 * et quand. La vérification est attachée au build : un nouveau run repart
 * « non vérifié ». L'auteur est résolu côté serveur depuis le PAT.
 * Un run sans aucun échec, ou dont chaque échec a un bug lié, est affiché
 * vérifié d'office, daté du run.
 */
export default function SuiteVerifyButton({ pipelineId, buildId, runId, suiteName, version, failed, totalTests, executionDate }) {
  const verifications = useSuiteVerifications();
  const suite = { pipelineId, buildId, runId, failed, totalTests, executionDate };
  const knownFailures = useKnownFailures([suite]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loading = verifications === null;
  const verification = verifications?.[suiteVerificationKey(pipelineId, buildId, suiteName)] || null;
  const auto = verification ? null : autoVerification(suite, getCoverage(knownFailures, suite));

  if (!buildId) return null;

  const verify = async () => {
    setSaving(true);
    setError(null);
    try {
      await suiteVerificationService.verify({ pipelineId, buildId, suiteName, version });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const unverify = async () => {
    if (!window.confirm(`Annuler l'analyse de ${verification.verifiedBy} ?`)) return;
    setSaving(true);
    setError(null);
    try {
      await suiteVerificationService.unverify({ pipelineId, buildId, suiteName });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 text-sm text-gray-400 border border-gray-200 rounded-md">
        <Loader2 className="w-4 h-4 animate-spin" />
        Chargement…
      </div>
    );
  }

  if (auto) {
    return (
      <div className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-green-50 text-green-800 border border-green-200">
        <CheckCircle className="w-4 h-4 flex-shrink-0 text-green-600" />
        <span className="flex-1 min-w-0 leading-tight">
          <span className="block font-medium">
            {auto.reason === 'known-bugs' ? 'Run analysé (échecs connus)' : 'Run analysé (sans erreur)'}
          </span>
          {auto.reason === 'known-bugs' && (
            <span className="block text-xs text-green-700">
              {auto.failedWithBug === 1 ? '1 échec, lié à un bug' : `${auto.failedWithBug} échecs, tous liés à un bug`}
            </span>
          )}
          {auto.verifiedAt && (
            <span className="block text-xs text-green-700">Run du {formatVerifiedAt(auto.verifiedAt)}</span>
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-2">
      {verification ? (
        <div
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-green-50 text-green-800 border border-green-200"
          title={verification.verifiedByEmail || undefined}
        >
          <CheckCircle className="w-4 h-4 flex-shrink-0 text-green-600" />
          <span className="flex-1 min-w-0 leading-tight">
            <span className="block break-words">
              Analysé par <span className="font-medium">{verification.verifiedBy}</span>
            </span>
            <span className="block text-xs text-green-700">le {formatVerifiedAt(verification.verifiedAt)}</span>
          </span>
          <button
            onClick={unverify}
            disabled={saving}
            className="p-0.5 rounded hover:bg-green-100 text-green-700 disabled:opacity-50"
            title="Annuler l'analyse"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
          </button>
        </div>
      ) : (
        <button
          onClick={verify}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-green-50 hover:text-green-700 hover:border-green-300 disabled:opacity-50 transition-colors"
          title="Indiquer que ce run a été analysé"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Circle className="w-4 h-4" />}
          Marquer ce run comme analysé
        </button>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
